import { Checkbox } from "@nextui-org/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
	Building2,
	Loader2,
	PlusCircleIcon,
	PlusIcon,
	RefreshCwIcon,
	SaveIcon,
	TrashIcon,
	TriangleAlertIcon,
	Undo2Icon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import Container from "@/components/ui/container";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { InputError } from "@/components/ui/inputError";
import { Label } from "@/components/ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import cacheKeys from "@/config/cache-keys";
import pages from "@/config/pages";
import {
	useCreateAccountStore,
	useUpdateAccountStore,
} from "@/hooks/accounts/useCreateAccountStore";
import { useProductDefinitions } from "@/hooks/useProductDefinitions";
import {
	computeProductDrift,
	type DriftReport,
	logProductDrift,
} from "@/lib/insuranceDrift";
import { cn } from "@/lib/utils";
import AppService from "@/services/AppService";
import type { APIResponse } from "@/types/APIresponse";
import type { AccountTree } from "@/types/accountTree/accountTree";
import type { CreateInsuranceFormData } from "@/types/insurance/insurance";
import { ContinueFromLastSave } from "./components/ContinueFromLastSave";
import { GroupDropdownMenu } from "./components/GroupDropdownMenu";
import { InsuranceModificationHistory } from "./components/InsuranceModificationHistory";
import { ParameterInput } from "./components/ParameterInput";
import { ResetFormDialog } from "./components/ResetFormDialog";
import { SearchCompanyInput } from "./components/SearchCompanyInput";
import { SingleAccountView } from "./components/SingleAccountView";

/** Stable comparison for insurance.groups (group assignment). */
const serializeInsuranceGroups = (groups: string[] | undefined): string => {
	const ids = [...(groups ?? [])];
	ids.sort();
	return JSON.stringify(ids);
};

const CreateMainAccountPage = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const service = new AppService();
	const { t } = useTranslation();

	const { mainAccountId } = useParams();

	const isCreateMode = location.pathname.includes("create");

	const createStore = useCreateAccountStore();
	const updateStore = useUpdateAccountStore();

	const store = isCreateMode ? createStore : updateStore;
	const queryClient = useQueryClient();

	const { data } = useQuery<APIResponse<AccountTree>>({
		queryKey: cacheKeys.SINGLE_MAIN_ACCOUNT(mainAccountId as string),
		queryFn: async () => {
			if (!mainAccountId)
				throw new Error(t("admin.insurances.messages.main-account-required"));
			return await service.getMainAccountTree(mainAccountId);
		},
		refetchOnMount: false,
		refetchInterval: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchIntervalInBackground: false,
		enabled: !isCreateMode,
	});

	// Backend-driven catalog of available insurance products. Seeds the
	// create-form's `insurances` list and provides default values used by
	// `editParameter` / `resetParameter` to detect alterations.
	const {
		templates: productDefinitionTemplates,
		isLoading: isLoadingProductDefinitions,
		isError: isProductDefinitionsError,
		refetch: refetchProductDefinitions,
	} = useProductDefinitions();

	useEffect(() => {
		if (productDefinitionTemplates.length === 0) return;
		// Push templates into both stores so the active store (create or
		// update) has them available for default-value lookups regardless of
		// which mode the user navigates into next.
		useCreateAccountStore
			.getState()
			.setProductDefinitions(productDefinitionTemplates);
		useUpdateAccountStore
			.getState()
			.setProductDefinitions(productDefinitionTemplates);
	}, [productDefinitionTemplates]);

	// Snapshot of param values as they were when last loaded/saved from the backend.
	// Used to detect whether the user has actually changed anything vs reverted.
	const paramSnapshotRef = useRef<Map<string, unknown>>(new Map());
	// Provider (leverandør) per insurance id — same purpose as param snapshots.
	const insuranceProviderSnapshotRef = useRef<Map<string, string>>(new Map());
	// Group ids assigned to each insurance (serialized for comparison).
	const insuranceGroupsSnapshotRef = useRef<Map<string, string>>(new Map());
	// Clear row dirty flags only when switching main account — not on every tree refetch.
	const lastDirtyResetMainAccountIdRef = useRef<string | undefined>(undefined);

	// Track which insurance rows have been edited in this session.
	// This is intentionally separate from `isAltered`, which only measures
	// "differs from default" — not "edited since last load/save".
	const [dirtyInsuranceIds, setDirtyInsuranceIds] = useState(new Set<string>());

	// Structural drift between the customer's saved rows and the current
	// product-definition catalog. Populated once per account load and used to
	// conditionally render the refresh button and populate the drift modal.
	const [driftReport, setDriftReport] = useState<DriftReport>({
		drifted: [],
		orphan: [],
	});
	const driftedById = useMemo(
		() => new Map(driftReport.drifted.map((d) => [d.id, d])),
		[driftReport],
	);

	// Insurance id whose refresh is pending user confirmation in the drift modal.
	const [confirmRefreshId, setConfirmRefreshId] = useState<string | null>(null);
	const confirmDrift = confirmRefreshId
		? driftedById.get(confirmRefreshId)
		: undefined;

	const getAccountState = () =>
		(isCreateMode ? useCreateAccountStore : useUpdateAccountStore).getState();

	// biome-ignore lint/correctness/useExhaustiveDependencies:>
	useEffect(() => {
		if (!data?.Result || !mainAccountId) {
			return;
		}
		// Wait for the product catalog before populating the store so the
		// merge in `setFullAccountTree` can append templates for product types
		// the customer doesn't yet have. If the catalog query resolves after
		// the tree query, this effect re-runs once `productDefinitionTemplates`
		// changes (added below as a dependency).
		if (productDefinitionTemplates.length === 0) {
			return;
		}
		const accountSwitched =
			lastDirtyResetMainAccountIdRef.current !== mainAccountId;
		if (accountSwitched) {
			lastDirtyResetMainAccountIdRef.current = mainAccountId;
		}

		store.setFullAccountTree(data.Result);

		// Passive drift diagnostic — emit once per customer load. Compares
		// each saved row's parameter set against the canonical catalog and
		// logs missing/extra param types or fully-orphan products. Uses the
		// raw API rows (not the merged store) so we don't flag the freshly
		// appended templates as drifted by definition.
		if (accountSwitched) {
			const report = computeProductDrift(
				data.Result.insurances,
				productDefinitionTemplates,
			);
			logProductDrift(report, data.Result.main_account.display_name, (type) =>
				t(`admin.insurances.type-to-name.${type}`),
			);
			setDriftReport(report);
		}

		// Snapshot from the merged store state (not from `data.Result.insurances`)
		// so that template rows appended for missing product types also get a
		// baseline. Without this they would appear pre-dirty on first render.
		const merged = getAccountState().insurances;
		const snapshot = new Map<string, unknown>();
		const providerSnap = new Map<string, string>();
		const groupsSnap = new Map<string, string>();
		for (const insurance of merged) {
			providerSnap.set(insurance.id, insurance.provider);
			groupsSnap.set(insurance.id, serializeInsuranceGroups(insurance.groups));
			for (const param of insurance.parameters ?? []) {
				snapshot.set(param.id, param.value);
			}
		}
		paramSnapshotRef.current = snapshot;
		insuranceProviderSnapshotRef.current = providerSnap;
		insuranceGroupsSnapshotRef.current = groupsSnap;

		if (accountSwitched) {
			setDirtyInsuranceIds(new Set());
		} else {
			// Refetch after a row save: keep other products' unsaved state — only drop
			// dirty flags for rows that now match the server snapshots.
			setDirtyInsuranceIds((prev) => {
				const next = new Set<string>();
				for (const id of prev) {
					const ins = getAccountState().insurances.find((i) => i.id === id);
					if (!ins) {
						continue;
					}
					const paramsMatch =
						ins.parameters?.every((p) => {
							const snap = paramSnapshotRef.current.get(p.id);
							return snap !== undefined && p.value === snap;
						}) ?? true;
					const providerMatch =
						!insuranceProviderSnapshotRef.current.has(ins.id) ||
						ins.provider === insuranceProviderSnapshotRef.current.get(ins.id);
					const groupsMatch =
						!insuranceGroupsSnapshotRef.current.has(ins.id) ||
						serializeInsuranceGroups(ins.groups) ===
							insuranceGroupsSnapshotRef.current.get(ins.id);
					if (!(paramsMatch && providerMatch && groupsMatch)) {
						next.add(id);
					}
				}
				return next;
			});
		}
		// adding `store` will trigger infinite re-renders
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, mainAccountId, productDefinitionTemplates]);

	const [isShowingResetModal, setIsShowingResetModal] = useState(false);
	const [openAccountIds, setOpenAccountIds] = useState<string[]>([]);
	const prevAccountsLength = useRef(store.accounts.length);

	const refreshSnapshotsFromStore = () => {
		const snapshot = new Map<string, unknown>();
		const providerSnap = new Map<string, string>();
		const groupsSnap = new Map<string, string>();
		for (const ins of store.insurances) {
			providerSnap.set(ins.id, ins.provider);
			groupsSnap.set(ins.id, serializeInsuranceGroups(ins.groups));
			for (const param of ins.parameters ?? []) {
				snapshot.set(param.id, param.value);
			}
		}
		paramSnapshotRef.current = snapshot;
		insuranceProviderSnapshotRef.current = providerSnap;
		insuranceGroupsSnapshotRef.current = groupsSnap;
	};

	const syncDirtyState = (insurance: CreateInsuranceFormData) => {
		const paramsMatch =
			insurance.parameters?.every((p) => {
				const snap = paramSnapshotRef.current.get(p.id);
				// A param not in the snapshot (e.g. dynamically injected) counts as dirty
				return snap !== undefined && p.value === snap;
			}) ?? true;
		const providerMatch =
			!insuranceProviderSnapshotRef.current.has(insurance.id) ||
			insurance.provider ===
				insuranceProviderSnapshotRef.current.get(insurance.id);
		const groupsMatch =
			!insuranceGroupsSnapshotRef.current.has(insurance.id) ||
			serializeInsuranceGroups(insurance.groups) ===
				insuranceGroupsSnapshotRef.current.get(insurance.id);
		const allMatchSnapshot = paramsMatch && providerMatch && groupsMatch;
		setDirtyInsuranceIds((prev) => {
			const next = new Set(prev);
			if (allMatchSnapshot) {
				next.delete(insurance.id);
			} else {
				next.add(insurance.id);
			}
			return next;
		});
	};

	const trackedStore = {
		...store,
		setProviderForInsurance: (insuranceID: string, provider: string) => {
			store.setProviderForInsurance(insuranceID, provider);
			const ins = getAccountState().insurances.find(
				(i) => i.id === insuranceID,
			);
			if (ins) {
				syncDirtyState(ins);
			}
		},
		setProviderForAllInsurances: (provider: string) => {
			store.setProviderForAllInsurances(provider);
			for (const ins of getAccountState().insurances) {
				syncDirtyState(ins);
			}
		},
		editParameter: (
			insurance: Parameters<typeof store.editParameter>[0],
			parameter: Parameters<typeof store.editParameter>[1],
		) => {
			store.editParameter(insurance, parameter);
			const ins = getAccountState().insurances.find(
				(i) => i.id === insurance.id,
			);
			if (ins) {
				syncDirtyState(ins);
			}
		},
		resetParameter: (
			insurance: Parameters<typeof store.resetParameter>[0],
			parameter: Parameters<typeof store.resetParameter>[1],
		) => {
			store.resetParameter(insurance, parameter);
			const ins = getAccountState().insurances.find(
				(i) => i.id === insurance.id,
			);
			if (ins) {
				syncDirtyState(ins);
			}
		},
		clearGroupsFromAllInsurances: () => {
			store.clearGroupsFromAllInsurances();
			for (const ins of getAccountState().insurances) {
				syncDirtyState(ins);
			}
		},
		assignGroupsToAllInsurances: () => {
			store.assignGroupsToAllInsurances();
			for (const ins of getAccountState().insurances) {
				syncDirtyState(ins);
			}
		},
		toggleGroupsForAllInsurances: () => {
			store.toggleGroupsForAllInsurances();
			for (const ins of getAccountState().insurances) {
				syncDirtyState(ins);
			}
		},
		clearGroupsFromInsurance: (insuranceID: string) => {
			store.clearGroupsFromInsurance(insuranceID);
			const ins = getAccountState().insurances.find(
				(i) => i.id === insuranceID,
			);
			if (ins) {
				syncDirtyState(ins);
			}
		},
		assignGroupsToInsurance: async (insuranceID: string) => {
			await store.assignGroupsToInsurance(insuranceID);
			const ins = getAccountState().insurances.find(
				(i) => i.id === insuranceID,
			);
			if (ins) {
				syncDirtyState(ins);
			}
		},
		toggleGroupsForInsurance: async (insuranceID: string) => {
			const object = getAccountState();
			if (object.isSelectedForInsurance(insuranceID)) {
				store.clearGroupsFromInsurance(insuranceID);
			} else {
				await store.assignGroupsToInsurance(insuranceID);
			}
			const ins = getAccountState().insurances.find(
				(i) => i.id === insuranceID,
			);
			if (ins) {
				syncDirtyState(ins);
			}
		},
		toggleSingleGroupForInsurance: async (
			insurance: CreateInsuranceFormData,
			groupID: string,
		) => {
			await store.toggleSingleGroupForInsurance(insurance, groupID);
			const ins = getAccountState().insurances.find(
				(i) => i.id === insurance.id,
			);
			if (ins) {
				syncDirtyState(ins);
			}
		},
		refreshProductParameters: (insuranceID: string) => {
			store.refreshProductParameters(insuranceID);
			const ins = getAccountState().insurances.find(
				(i) => i.id === insuranceID,
			);
			if (ins) {
				syncDirtyState(ins);
			}
		},
	};

	useEffect(() => {
		if (store.accounts.length > prevAccountsLength.current) {
			const newAccount = store.accounts[store.accounts.length - 1];
			setOpenAccountIds([newAccount.id]);
		}
		prevAccountsLength.current = store.accounts.length;
	}, [store.accounts]);

	const handleOpenChange = (accountId: string, open: boolean) => {
		setOpenAccountIds((prev) => {
			if (open) {
				return [...prev, accountId];
			} else {
				return prev.filter((id) => id !== accountId);
			}
		});
	};

	async function handleCreate() {
		const data = store.formatForCreate();

		if (!store.mainAccount.display_name) {
			toast.error(t("admin.insurances.messages.main-account-required"));
			return;
		}

		if (store.accounts.length === 0) {
			toast.error(t("admin.insurances.messages.at-least-one-account"));
			return;
		}

		for (const [index, account] of store.accounts.entries()) {
			let fieldValidationError: string | null = null;
			const accountDisplayName = account.name || `Account ${index + 1}`;

			if (!account.name) {
				fieldValidationError = t(
					"admin.insurances.messages.account-name-required",
					{ index: index + 1 },
				);
			} else if (!account.orgnr) {
				fieldValidationError = t(
					"admin.insurances.messages.account-orgnr-required",
					{ accountName: accountDisplayName },
				);
			} else if (!account.contactPerson) {
				fieldValidationError = t(
					"admin.insurances.messages.account-contact-required",
					{ accountName: accountDisplayName },
				);
			} else if (!account.email) {
				fieldValidationError = t(
					"admin.insurances.messages.account-email-required",
					{ accountName: accountDisplayName },
				);
			} else if (!account.phone) {
				fieldValidationError = t(
					"admin.insurances.messages.account-phone-required",
					{ accountName: accountDisplayName },
				);
			} else if (account.error) {
				fieldValidationError = t(
					"admin.insurances.messages.account-validation-error",
					{ accountName: accountDisplayName, error: account.error },
				);
			}

			if (fieldValidationError) {
				toast.error(fieldValidationError);
				return;
			}

			const accountGroups = store.groups.filter(
				(group) => group.accountID === account.id,
			);
			if (accountGroups.length === 0) {
				toast.error(
					t("admin.insurances.messages.account-no-groups", {
						accountName: accountDisplayName,
					}),
				);
				return;
			}

			const hasNamedGroup = accountGroups.some(
				(group) => group.name && group.name.trim() !== "",
			);
			if (!hasNamedGroup) {
				toast.error(
					t("admin.insurances.messages.account-group-name-missing", {
						accountName: accountDisplayName,
					}),
				);
				return;
			}
		}

		const ysInsurance = store.insurances.find(
			(insurance) => insurance.type === "wc",
		);

		if (ysInsurance && store.isSelectedForInsurance(ysInsurance.id)) {
			if (!ysInsurance.provider) {
				toast.error(t("admin.insurances.messages.provider-required"));
				return;
			}
		} else {
			toast.error(t("admin.insurances.messages.wc-required"));
			return;
		}

		try {
			const promise = service.createMainAccountTree(data);
			toast.promise(promise, {
				loading: t("admin.insurances.messages.creating-client"),
				success: t("admin.insurances.messages.client-created"),
				error: t("admin.insurances.messages.something-went-wrong"),
			});
			await promise;
			store.reset();
			navigate(pages.Admin);
		} catch (error) {
			console.error("Error uploading data:", error);
		}
	}

	async function handleUpdate() {
		const data = store.formatForUpdate();

		if (!mainAccountId) {
			console.error(t("admin.insurances.messages.main-account-required"));
			toast.error(t("admin.insurances.messages.main-account-required"));
			return;
		}

		try {
			const promise = service.updateMainAccountTree(mainAccountId, data);
			toast.promise(promise, {
				loading: t("admin.insurances.messages.updating-client"),
				success: t("admin.insurances.messages.client-updated"),
				error: t("admin.insurances.messages.something-went-wrong"),
			});
			await promise;
			refreshSnapshotsFromStore();
			setDirtyInsuranceIds(new Set());
			await queryClient.invalidateQueries({
				queryKey: cacheKeys.SINGLE_MAIN_ACCOUNT(mainAccountId),
			});
		} catch (error) {
			console.error("Error uploading data:", error);
		}
	}

	async function handleSaveInsurance(insuranceId: string) {
		if (!mainAccountId) return;
		try {
			const data = store.formatForUpdate();
			await service.updateMainAccountTree(mainAccountId, data);
			// Update the snapshot so reverts from this point compare against the
			// newly saved values, not the original load values.
			const saved = store.insurances.find((i) => i.id === insuranceId);
			if (saved?.parameters) {
				for (const param of saved.parameters) {
					paramSnapshotRef.current.set(param.id, param.value);
				}
			}
			if (saved) {
				insuranceProviderSnapshotRef.current.set(saved.id, saved.provider);
				insuranceGroupsSnapshotRef.current.set(
					saved.id,
					serializeInsuranceGroups(saved.groups),
				);
			}
			setDirtyInsuranceIds((prev) => {
				const next = new Set(prev);
				next.delete(insuranceId);
				return next;
			});
			await queryClient.invalidateQueries({
				queryKey: cacheKeys.SINGLE_MAIN_ACCOUNT(mainAccountId),
			});
			const insuranceName = saved
				? t(`admin.insurances.type-to-name.${saved.type}`)
				: t("admin.insurances.messages.client-updated");
			toast.success(
				`${insuranceName} — ${t("admin.insurances.messages.client-updated")}`,
			);
		} catch (error) {
			console.error("Error saving insurance:", error);
			toast.error(t("admin.insurances.messages.something-went-wrong"));
		}
	}

	// ============================================
	//   MAIN COMPONENT HOLDING IT ALL TOGETHER
	// ============================================

	// In create mode we depend on the backend product catalog to seed the
	// form. While it loads (and only when there is nothing in the store yet —
	// e.g. no persisted draft), show a spinner so the user does not see an
	// empty insurances table. In update mode the existing tree fetch handles
	// rendering, and the templates load in parallel without blocking.
	if (
		isCreateMode &&
		store.insurances.length === 0 &&
		isLoadingProductDefinitions
	) {
		return (
			<Container className="flex justify-center items-center py-20">
				<Loader2 className="h-8 w-8 animate-spin text-howden-blue" />
				<span className="ml-2">
					{t("admin.insurances.messages.loading-products")}
				</span>
			</Container>
		);
	}

	if (
		isCreateMode &&
		store.insurances.length === 0 &&
		isProductDefinitionsError
	) {
		return (
			<Container className="flex flex-col justify-center items-center gap-4 py-20">
				<span>{t("admin.insurances.messages.something-went-wrong")}</span>
				<Button onClick={() => refetchProductDefinitions()}>
					{t("admin.button.retry") ?? "Try again"}
				</Button>
			</Container>
		);
	}

	return (
		<div className="bg-soft-white dark:bg-[#121212] min-h-screen">
			<ContinueFromLastSave
				store={trackedStore}
				onReset={() => setIsShowingResetModal(true)}
			/>
			<ResetFormDialog
				store={trackedStore}
				open={isShowingResetModal}
				onOpenChange={setIsShowingResetModal}
			/>
			<Container className="flex flex-col gap-8 mx-auto px-4">
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem>
							<Link
								to={pages.Admin}
								className="text-howden-grey hover:text-gold dark:hover:text-gold-95"
							>
								{t("admin.home")}
							</Link>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<Link to={pages.AdminMainAccountCreate}>
								{isCreateMode
									? t("admin.create.title-1")
									: t("admin.create.title-2")}
							</Link>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>
				<div className="mb-8 flex justify-between items-center">
					<h1 className="font-bold text-3xl leading-tight">
						{isCreateMode
							? t("admin.create.title-1")
							: t("admin.create.title-2")}
					</h1>
					{isCreateMode && (
						<Button
							variant="tertiary"
							onClick={() => setIsShowingResetModal(true)}
						>
							<Undo2Icon />
							{t("admin.button.reset")} {t("admin.create.form.form")}
						</Button>
					)}
				</div>

				{/* Main Account Section */}
				<section className="flex flex-col gap-2">
					<h2 className="text-xl font-medium">{t("admin.create.form.name")}</h2>
					<div className="border p-6 rounded-lg flex flex-col gap-4 bg-white dark:bg-[#1d1e1e] dark:border-black">
						<Label>{t("admin.create.form.name")}</Label>
						<SearchCompanyInput
							value={trackedStore.mainAccount.display_name}
							onChange={(value) => {
								let error: string | null = null;
								if (value.length < 1) {
									error = t("admin.create.form.error.name-min");
								}
								if (value.length > 60) {
									error = t("admin.create.form.error.name-max");
								}
								trackedStore.setMainAccount({
									...trackedStore.mainAccount,
									display_name: value.toUpperCase(),
									error: error,
								});
							}}
							onSelect={(value) => {
								trackedStore.setMainAccount({
									...trackedStore.mainAccount,
									display_name: value.company_name,
									orgnr: value.business_id,
									error: null,
								});
							}}
							placeholder={t("admin.create.form.name")}
						/>
						<InputError>{trackedStore.mainAccount.error}</InputError>
					</div>
				</section>

				{/* Accounts Section */}
				<section className="flex flex-col gap-2 border-gray-200 dark:border-gray-700">
					<h2 className="text-xl font-medium">Accounts</h2>
					<div className="flex flex-col gap-12">
						{trackedStore.accounts.map((account) => {
							return (
								<SingleAccountView
									store={trackedStore}
									key={account.id}
									account={account}
									handleChange={trackedStore.editAccount}
									handleDelete={trackedStore.removeAccount}
									isOpen={openAccountIds.includes(account.id)}
									onOpenChange={(open) => handleOpenChange(account.id, open)}
								/>
							);
						})}
					</div>
					<Button
						className="w-fit mx-auto mt-4"
						onClick={trackedStore.addAccount}
					>
						<PlusIcon />
						{t("admin.button.new")} Account
					</Button>
				</section>

				{/* Insurances Section */}
				<section className="flex flex-col gap-2">
					<h2 className="text-xl font-medium">{t("admin.insurances.title")}</h2>
					<Table containerClassName="bg-white dark:bg-black rounded-lg border">
						<TableHeader>
							<TableRow>
								<TableHead role="checkbox" className="w-[20px]">
									<Checkbox
										isSelected={trackedStore.isSelectedForAllInsurances()}
										onValueChange={trackedStore.toggleGroupsForAllInsurances}
									/>
								</TableHead>
								<TableHead className="w-[220px]">
									{t("admin.insurances.insurance")}
								</TableHead>
								<TableHead className="w-[730px]">
									{t("admin.insurances.parameters.title")}
								</TableHead>
								<TableHead className="w-[200px]">
									{t("admin.insurances.groups.title")}
								</TableHead>
								<TableHead className="w-[150px]">
									{t("admin.insurances.actions")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<AnimatePresence initial={false}>
							<TableBody>
								{store.insurances.map((insurance, index) => {
									const isSelected =
										trackedStore.isSelectedForInsurance(insurance.id);
									return (
										<motion.tr
											key={insurance.id}
											className={cn(
												"border-b transition-colors hover:bg-zinc-100/50 data-[state=selected]:bg-zinc-100 dark:hover:bg-zinc-800/50 dark:data-[state=selected]:bg-zinc-800 relative group",
											)}
											layout
											initial={{ opacity: 0, height: 0 }}
											animate={{ opacity: 1, height: "auto" }}
											exit={{ opacity: 0, height: 0 }}
											transition={{ duration: 0.3, ease: "easeInOut" }}
										>
											<TableCell role="checkbox">
												<Checkbox
													className="pointer-events-auto"
													isSelected={trackedStore.isSelectedForInsurance(
														insurance.id,
													)}
													onValueChange={() =>
														trackedStore.toggleGroupsForInsurance(insurance.id)
													}
												/>
											</TableCell>
											<TableCell
												className={cn(
													"transition-opacity",
													!trackedStore.isSelectedForInsurance(insurance.id) &&
														"opacity-50 pointer-events-none transition-opacity",
												)}
											>
												<div className="flex flex-col gap-1.5">
													<span className="font-medium text-sm">
														{t(
															`admin.insurances.type-to-name.${insurance.type}`,
														)}
													</span>
													{isSelected && (
														<div className="transition-opacity">
															<div className="flex items-center gap-1 mb-1 text-xs text-gray-500 dark:text-gray-400">
																<Building2 className="h-3 w-3 shrink-0" />
																<span>{t("admin.insurances.provider")}</span>
															</div>
															<div className="w-44">
																<SearchCompanyInput
																	value={insurance.provider}
																	onChange={(value) => {
																		if (insurance.type === "wc") {
																			trackedStore.setProviderForAllInsurances(
																				value,
																			);
																		} else {
																			trackedStore.setProviderForInsurance(
																				insurance.id,
																				value,
																			);
																		}
																	}}
																	placeholder={t("admin.insurances.provider")}
																	onSelect={(company) => {
																		if (insurance.type === "wc") {
																			trackedStore.setProviderForAllInsurances(
																				company.company_name,
																			);
																		} else {
																			trackedStore.setProviderForInsurance(
																				insurance.id,
																				company.company_name,
																			);
																		}
																	}}
																/>
															</div>
														</div>
													)}
												</div>
											</TableCell>
											<TableCell
												className={cn(
													"transition-all",
													isSelected
														? "flex flex-wrap items-start gap-2"
														: "py-1 text-xs italic text-gray-400 dark:text-gray-500",
												)}
											>
												{isSelected ? (
													(insurance.parameters ?? []).map((parameter) => (
														<ParameterInput
															store={trackedStore}
															key={parameter.id}
															insurance={insurance}
															parameter={parameter}
															alwaysExpanded
														/>
													))
												) : (
													<span>{t("admin.insurances.disabled")}</span>
												)}
											</TableCell>
											<TableCell className={cn("transition-opacity")}>
												<GroupDropdownMenu
													store={trackedStore}
													insurance={insurance}
												/>
											</TableCell>
											<TableCell className={cn("transition-opacity")}>
												<div className="flex flex-wrap items-center gap-2">
													{/* Per-row save button — visible only when edited in this session */}
													{dirtyInsuranceIds.has(insurance.id) && (
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="default"
																	size={"icon"}
																	className="bg-green-600 hover:bg-green-700 text-white"
																	onClick={() =>
																		handleSaveInsurance(insurance.id)
																	}
																>
																	<SaveIcon />
																</Button>
															</TooltipTrigger>
															<TooltipContent>
																{t("admin.insurances.save-row-tooltip", {
																	productName: t(
																		`admin.insurances.type-to-name.${insurance.type}`,
																	),
																})}
															</TooltipContent>
														</Tooltip>
													)}
													{/* Button to add an insurance under the current one */}
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="secondary"
																size={"icon"}
																onClick={() =>
																	trackedStore.addInsuranceBelow(index)
																}
															>
																<PlusCircleIcon />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															{t("admin.insurances.tooltips.add-insurance")}
														</TooltipContent>
													</Tooltip>
													<InsuranceModificationHistory insurance={insurance} />
													{insurance.mode === "update" &&
														driftedById.has(insurance.id) && (
															<Tooltip>
																<TooltipTrigger asChild>
																	<Button
																		variant="tertiary"
																		size={"icon"}
																		onClick={() =>
																			setConfirmRefreshId(insurance.id)
																		}
																	>
																		<RefreshCwIcon />
																	</Button>
																</TooltipTrigger>
																<TooltipContent>
																	{t(
																		"admin.insurances.tooltips.refresh-drifted",
																	)}
																</TooltipContent>
															</Tooltip>
														)}
													{/* Conditionally render a button to remove the insurance */}
													{insurance.isRemovable && (
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="destructive"
																	size={"icon"}
																	onClick={() =>
																		trackedStore.removeInsurance(insurance.id)
																	}
																>
																	<TrashIcon />
																</Button>
															</TooltipTrigger>
															<TooltipContent>
																{t(
																	"admin.insurances.tooltips.remove-insurance",
																)}
															</TooltipContent>
														</Tooltip>
													)}
												</div>
											</TableCell>
										</motion.tr>
									);
								})}
							</TableBody>
						</AnimatePresence>
					</Table>
				</section>

				{/* Drift confirmation modal — shown when the refresh button is
				    clicked on a row whose params have drifted from the catalog. */}
				<Dialog
					open={!!confirmRefreshId}
					onOpenChange={(open) => {
						if (!open) setConfirmRefreshId(null);
					}}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>
								{t("admin.insurances.drift-modal.title")}
							</DialogTitle>
							<DialogDescription>
								{t("admin.insurances.drift-modal.description")}
							</DialogDescription>
							{confirmDrift && confirmDrift.extraParams.length > 0 && (
								<p className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
									<TriangleAlertIcon className="size-4 shrink-0" />
									{t("admin.insurances.drift-modal.description-warning")}
								</p>
							)}
						</DialogHeader>

						{confirmDrift && (
							<div className="space-y-4 text-sm">
								{confirmDrift.missingParams.length > 0 && (
									<div className="space-y-1">
										<p className="font-medium">
											{t("admin.insurances.drift-modal.missing-heading")}
										</p>
										<ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
											{confirmDrift.missingParams.map((p) => (
												<li key={p}>{p}</li>
											))}
										</ul>
									</div>
								)}
								{confirmDrift.extraParams.length > 0 && (
									<div className="space-y-1">
										<p className="font-medium">
											{t("admin.insurances.drift-modal.extra-heading")}
										</p>
										<ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
											{confirmDrift.extraParams.map((p) => (
												<li key={p}>{p}</li>
											))}
										</ul>
									</div>
								)}
							</div>
						)}

						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setConfirmRefreshId(null)}
							>
								{t("admin.insurances.drift-modal.cancel")}
							</Button>
							<Button
								variant="destructive"
								onClick={() => {
									if (confirmRefreshId) {
										trackedStore.refreshProductParameters(confirmRefreshId);
										setDriftReport((prev) => ({
											...prev,
											drifted: prev.drifted.filter(
												(d) => d.id !== confirmRefreshId,
											),
										}));
										setConfirmRefreshId(null);
									}
								}}
							>
								<RefreshCwIcon />
								{t("admin.insurances.drift-modal.confirm")}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Save Button */}
				<div className="flex items-center justify-center gap-4">
					<Button
						onClick={() => (isCreateMode ? handleCreate() : handleUpdate())}
					>
						<SaveIcon />{" "}
						{isCreateMode
							? t("admin.button.save-account")
							: t("admin.button.save-changes")}
					</Button>
				</div>
			</Container>
		</div>
	);
};

export default CreateMainAccountPage;
